import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import LabelledColumn from '../../atoms/LabelledColumn';
import StatusBadge from '../../atoms/StatusBadge';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import BinSetLoadingSVG from '../../atoms/BinSetLoadingSVG';
import FeedFloToolTip from '../../atoms/FeedFloToolTip';
import { BoxIcon } from '../../atoms/Icons';
import BinSetInventoryChart from '../../molecules/BinSetInventoryChart';
import MultiBinVisual from '../../molecules/MultiBinVisual/MultiBinVisual';

import { convertGramsToLargeUnits } from '../../utils/unitConversion';
import { DATE_FORMAT_WEEKDAY_SHORT_MONTH_DAY, DATE_TIME_FORMAT_FULL } from '../../utils/dates';
import { censorTypes, useCensor } from '../../utils/hooks/useCensor/useCensor';
import { TripStatusColor, TripStatusNameConverter } from '../../pages/FeedOrdersPage/enums';
import { BinState, BinStateColour, OrderState } from '../../utils/binSetConstants';
import { calcBinState, calcEmptyIn, splitBinSetLevelGramsByBinCapacities } from '../../utils/binSetUtils';

import './WideBinSetStatusCard.scss';
import { Link } from 'react-router-dom';
import withStaffOnlyIdWrapper from '../../utils/withStaffOnlyIdWrapper';

const PER_BIN_CHART_HEIGHT = 140;

/** True when `per_bin_level_in_grams` is present and safe to drive **Per Bin** UI. */
function hasValidPerBinLevels(map) {
  if (map == null || typeof map !== 'object' || Array.isArray(map)) return false;
  return Object.values(map).some((v) => typeof v === 'number' && Number.isFinite(v));
}


/** Min/max unix seconds across `{ t }` points in the given series arrays. */
function chartDomainFromSeries(...seriesArrays) {
  const timestamps = seriesArrays
    .flat()
    .filter((p) => p && Number.isFinite(p.t))
    .map((p) => p.t);
  if (!timestamps.length) return null;
  return { start: Math.min(...timestamps), end: Math.max(...timestamps) };
}

/** Shared x-axis domain for all per-bin charts in one card (explicit props, then all per-bin data, then aggregate). */
function resolvePerBinChartDomain(
  perBinCharts,
  chartStartTime,
  chartEndTime,
  aggregateSeries,
) {
  if (chartStartTime && chartEndTime) {
    return { start: chartStartTime, end: chartEndTime };
  }

  const allPerBinSeries = perBinCharts.flatMap((c) => [
    c.step_bin_level_history,
    c.projection_past_deliveries,
    c.projection_with_scheduled,
    c.projection_with_recommended,
  ]);
  const fromPerBin = chartDomainFromSeries(...allPerBinSeries);
  if (fromPerBin) return fromPerBin;

  const fromPerBinSetSeries = chartDomainFromSeries(
    aggregateSeries.step_bin_level_history,
    aggregateSeries.projection_past_deliveries,
    aggregateSeries.projection_with_scheduled,
    aggregateSeries.projection_with_recommended,
  );
  if (fromPerBinSetSeries) return fromPerBinSetSeries;

  // if all else fails, show the last 2 weeks
  return { start: dayjs.tz().startOf('day').subtract(2, 'week').unix(), end: dayjs.tz().startOf('day').add(1, 'day').unix() };
}

/** One per-bin {@link BinSetInventoryChart} prop bundle per bin that has any series data. */
function buildPerBinCharts(bins, perBinChartData) {
  if (!perBinChartData) return { perBinCharts: [], showPerBinCharts: false };

  // The per-bin maps may be explicitly `null` on the prediction row (no JSONB), so use `||` to
  // fall back to `{}` — destructuring defaults only catch `undefined`.
  const pastByBinId = perBinChartData.pastByBinId || {};
  const deliveredByBinId = perBinChartData.deliveredByBinId || {};
  const scheduledByBinId = perBinChartData.scheduledByBinId || {};
  const recommendedByBinId = perBinChartData.recommendedByBinId || {};
  const perBinCharts = [];

  for (const bin of bins) {
    const binId = bin.id;
    if (!binId) continue;

    const past = pastByBinId[binId];
    const delivered = deliveredByBinId[binId];
    const scheduled = scheduledByBinId[binId];
    const recommended = recommendedByBinId[binId];

    const hasAny =
      past?.length > 0 ||
      delivered?.length > 0 ||
      scheduled?.length > 0 ||
      recommended?.length > 0;
    if (!hasAny) continue;

    perBinCharts.push({
      binId,
      binName: bin.name || '?',
      step_bin_level_history: past || [],
      projection_past_deliveries: delivered || [],
      projection_with_scheduled: scheduled || [],
      projection_with_recommended: recommended || [],
      capacityGrams: bin.capacityInGrams || 0,
    });
  }

  return { perBinCharts, showPerBinCharts: perBinCharts.length > 0 };
}

function WideBinSetStatusCard({
  className = '',
  loading = false,
  bins = [],
  binSetLevelInGrams = null,
  emptyAt = 0,
  runout_prevention_bin_set_level_in_grams,
  earliest_delivery_bin_set_level_in_grams,
  isMetric = false,
  order,
  binSetId = '',
  lastUpdatedAt = null,
  // Chart data props
  step_bin_level_history = [],
  projection_past_deliveries = [],
  projection_with_scheduled = [],
  projection_with_recommended = [],
  chartStartTime = 0,
  chartEndTime = 0,
  // Truth data props
  knownBinLevelData = [],
  displayTruthData = false,
  /** Per-bin series maps from a `bin_set_level_prediction` row (v3 `per_bin_*` JSONB columns). */
  perBinChartData = null,
  /** From prediction `per_bin_level_in_grams` — **Per Bin** inventory when valid. */
  perBinLevelInGrams = null,
}) {
  const { censor } = useCensor();

  // Use the chart's data-derived domain whenever any series has data; otherwise fall back to
  // explicit `chartStartTime`/`chartEndTime` props passed by the caller.
  const useAutoDomain = [
    step_bin_level_history,
    projection_past_deliveries,
    projection_with_scheduled,
    projection_with_recommended,
  ].some((a) => Array.isArray(a) && a.length > 0);
  const chartStart = useAutoDomain ? 0 : chartStartTime;
  const chartEnd = useAutoDomain ? 0 : chartEndTime;

  const { perBinCharts, showPerBinCharts } = useMemo(
    () => buildPerBinCharts(bins, perBinChartData),
    [bins, perBinChartData],
  );

  const perBinChartDomain = useMemo(() => {
    if (!showPerBinCharts) return null;
    return resolvePerBinChartDomain(perBinCharts, chartStartTime, chartEndTime, {
      step_bin_level_history,
      projection_past_deliveries,
      projection_with_scheduled,
      projection_with_recommended,
    });
  }, [
    showPerBinCharts,
    perBinCharts,
    chartStartTime,
    chartEndTime,
    step_bin_level_history,
    projection_past_deliveries,
    projection_with_scheduled,
    projection_with_recommended,
  ]);

  // Resolve per-bin inventory grams. **Per Bin** uses `perBinLevelInGrams` directly;
  // **Per Bin Set** falls back to per-bin past series, splitting the aggregate by capacity for
  // bins missing a series row. Per-bin Remaining rows render only when 2+ bins AND a real
  // per-bin signal exists (Per Bin map valid OR per-bin past series non-empty).
  const { binsWithGrams, perBinRemainingRows } = useMemo(() => {
    const validMap = hasValidPerBinLevels(perBinLevelInGrams);
    const pastByBinId = perBinChartData?.pastByBinId;
    const hasPerBinPastSeries =
      pastByBinId != null &&
      typeof pastByBinId === 'object' &&
      Object.values(pastByBinId).some((s) => Array.isArray(s) && s.length > 0);
    const splitGrams = !validMap ? splitBinSetLevelGramsByBinCapacities(bins, binSetLevelInGrams) : null;

    const withGrams = bins.map((bin, i) => {
      let levelGrams = null;
      const raw = bin.id != null ? perBinLevelInGrams?.[bin.id] : undefined;
      levelGrams = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
      if (levelGrams == null && splitGrams) {
        levelGrams = splitGrams[i];
      }

      return { ...bin, levelGrams };
    });

    const showPerBinRemaining = bins.length > 1 && (validMap || hasPerBinPastSeries);
    const remainingRows = showPerBinRemaining
      ? withGrams.map((bin) => ({
        key: bin.id || String(bin.name),
        name: censor(bin.name || '?', censorTypes.bin),
        levelStr:
          bin.levelGrams != null ? convertGramsToLargeUnits(isMetric, bin.levelGrams, 1) : '—',
        capStr: convertGramsToLargeUnits(isMetric, bin.capacityInGrams || 0, 0),
      }))
      : null;

    return { binsWithGrams: withGrams, perBinRemainingRows: remainingRows };
  }, [bins, perBinChartData, binSetLevelInGrams, perBinLevelInGrams, isMetric, censor]);

  if (loading) {
    return (
      <div className={`WideBinSetStatusCard ${className}`}>
        <div className="WideBinSetStatusCard-card WideBinSetStatusCard-loading">
          <BinSetLoadingSVG />
          <LoadingSkeleton className="WideBinSetStatusCard-title WideBinSetStatusCard-loadingShort" />
          <LoadingSkeleton className="MultiBinVisual-binSetIcon WideBinSetStatusCard-loadingIcon" />
          <LabelledColumn
            className="WideBinSetStatusCard-feedLineNames WideBinSetStatusCard-loadingMid"
            heading="Feed Lines"
            loading
          />
          <LabelledColumn
            className="WideBinSetStatusCard-capacity WideBinSetStatusCard-loadingMid"
            heading="Remaining"
            loading
          />
          <LabelledColumn
            className="WideBinSetStatusCard-emptyIn WideBinSetStatusCard-loadingMid"
            heading="Empty In"
            loading
          />
          <LabelledColumn
            className="WideBinSetStatusCard-lastDelivery WideBinSetStatusCard-loadingMid"
            heading="Last Delivery"
            loading
          />
        </div>
      </div>
    );
  }

  const binNames = bins.map(({ name }) => censor(name, censorTypes.bin) || '?');
  const name = `Bin${binNames.length > 1 ? 's' : ''} ${binNames.join(', ')}`;

  const capacityInGrams = bins.reduce((sum, { capacityInGrams = 0 }) => sum + capacityInGrams, 0);
  const capacityInLargeUnits = convertGramsToLargeUnits(isMetric, capacityInGrams, 0);
  const displayLevelGrams = binSetLevelInGrams;
  const binSetLevelInLargeUnits =
    displayLevelGrams != null ? convertGramsToLargeUnits(isMetric, displayLevelGrams, 1) : '?';

  const { emptyInHeading, emptyInString } = calcEmptyIn(emptyAt, displayLevelGrams);

  let orderTime = order?.delivered_at || order?.ordered_at;

  const binState = calcBinState(
    displayLevelGrams,
    earliest_delivery_bin_set_level_in_grams,
    runout_prevention_bin_set_level_in_grams,
  );
  const binStateErrorHighlight =
    BinState.ERROR === binState && OrderState.SCHEDULED !== order?.status && OrderState.SHIPPED !== order?.status;

  const binSetName = name;
  const IdWrappedTitle = withStaffOnlyIdWrapper(binSetName);

  // Create dynamic order header text based on order status
  const getOrderHeaderText = (orderStatus) => {
    switch (orderStatus) {
      case OrderState.RECOMMENDED:
        return 'Order Recommended';
      case OrderState.SHIPPED:
        return 'Order Shipped';
      case OrderState.SCHEDULED:
        return 'Order Scheduled';
      case OrderState.DELIVERED:
        return 'Order Delivered';
      default:
        return 'Order Status';
    }
  };

  // Get status color using existing utility
  const getOrderStatusColor = (orderStatus) => {
    // Convert status to the format expected by TripStatusColor
    const convertedStatus = TripStatusNameConverter[orderStatus];
    return TripStatusColor[convertedStatus] || '#9ca3af'; // Gray fallback
  };

  return (
    <div className="WideBinSetStatusCard">
      <div className={`WideBinSetStatusCard-card`}>
        <div className="WideBinSetStatusCard-leftContent">
          <div className="WideBinSetStatusCard-titleRow">
            <span className="WideBinSetStatusCard-title WideBinSetStatusCard-titleText">
              <IdWrappedTitle data={binSetId} hoverText="Copy Bin Set ID" />
            </span>
          </div>
          <MultiBinVisual bins={binsWithGrams} iconFillColour={BinStateColour[binState]} />
          <LabelledColumn className="WideBinSetStatusCard-capacity" heading="Remaining">
            {perBinRemainingRows ? (
              <ul className="WideBinSetStatusCard-perBinLevels">
                {perBinRemainingRows.map(({ key, name, levelStr, capStr }) => (
                  <li key={key} className="WideBinSetStatusCard-perBinLevelItem">
                    <span className="WideBinSetStatusCard-perBinLevelName">{name}:</span>
                    <span className="WideBinSetStatusCard-perBinLevelValue">{levelStr}</span>
                    <span className="WideBinSetStatusCard-capacityText">{` / ${capStr} ${isMetric ? 'Tonnes' : 'Tons'
                      }`}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <>
                <span className="WideBinSetStatusCard-capacityStrongText">{binSetLevelInLargeUnits}</span>
                <span className="WideBinSetStatusCard-capacityText">{` / ${capacityInLargeUnits} ${isMetric ? 'Tonnes' : 'Tons'
                  }`}</span>
              </>
            )}
          </LabelledColumn>
          <LabelledColumn className="WideBinSetStatusCard-emptyIn" heading={emptyInHeading}>
            <span className="WideBinSetStatusCard-contentText">
              {binStateErrorHighlight ? (
                <StatusBadge text={emptyInString.toUpperCase()} status="error" />
              ) : (
                emptyInString
              )}
            </span>
          </LabelledColumn>
          {order && (
            <LabelledColumn className="WideBinSetStatusCard-orderStatus" heading={getOrderHeaderText(order.status)}>
              <Link to={`/FeedDesk?trip=${order.trip_id}`} className="WideBinSetStatusCard-orderLink">
                <div className="WideBinSetStatusCard-orderContent">
                  <div
                    className="WideBinSetStatusCard-statusCircle"
                    style={{ backgroundColor: getOrderStatusColor(order.status) }}
                  />
                  <span className="WideBinSetStatusCard-contentText">
                    {dayjs.unix(orderTime).format(DATE_FORMAT_WEEKDAY_SHORT_MONTH_DAY)}
                  </span>
                </div>
                <BoxIcon className="WideBinSetStatusCard-chevronIcon" size={16} />
              </Link>
            </LabelledColumn>
          )}
        </div>
        <div className="WideBinSetStatusCard-rightContent">
          <div className="WideBinSetStatusCard-titleActions">
            <FeedFloToolTip
              description={`${lastUpdatedAt ? dayjs.tz(lastUpdatedAt * 1000).format(DATE_TIME_FORMAT_FULL) : 'Unknown'
                }`}
              position="left"
            >
              <span className="WideBinSetStatusCard-lastUpdated">
                {lastUpdatedAt && <>Last updated: {dayjs.tz(lastUpdatedAt * 1000).fromNow()}</>}
                {!lastUpdatedAt && <>Last updated: Unknown</>}
              </span>
            </FeedFloToolTip>
          </div>
          {!showPerBinCharts && (
            <BinSetInventoryChart
              step_bin_level_history={step_bin_level_history}
              projection_past_deliveries={projection_past_deliveries}
              projection_with_scheduled={projection_with_scheduled}
              projection_with_recommended={projection_with_recommended}
              chartStartTime={chartStart}
              chartEndTime={chartEnd}
              isMetric={isMetric}
              chartHeight={150}
              knownBinLevelData={knownBinLevelData}
              displayTruthData={displayTruthData}
              totalBinCapacity={capacityInGrams}
            />
          )}
          {showPerBinCharts && perBinChartDomain && (
            <div className="WideBinSetStatusCard-chartStack">
              {perBinCharts.map(
                (
                  {
                    binId,
                    binName,
                    step_bin_level_history: perBinStep,
                    projection_past_deliveries: perBinPast,
                    projection_with_scheduled: perBinSched,
                    projection_with_recommended: perBinRec,
                    capacityGrams,
                  },
                  index,
                ) => (
                  <BinSetInventoryChart
                    key={binId}
                    step_bin_level_history={perBinStep}
                    projection_past_deliveries={perBinPast}
                    projection_with_scheduled={perBinSched}
                    projection_with_recommended={perBinRec}
                    chartHeading={`Bin ${censor(binName, censorTypes.bin)}`}
                    chartStartTime={perBinChartDomain.start}
                    chartEndTime={perBinChartDomain.end}
                    isMetric={isMetric}
                    chartHeight={PER_BIN_CHART_HEIGHT}
                    knownBinLevelData={[]}
                    displayTruthData={false}
                    totalBinCapacity={capacityGrams}
                    hideXAxis={index < perBinCharts.length - 1}
                  />
                ),
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

WideBinSetStatusCard.propTypes = {
  className: PropTypes.string,
  loading: PropTypes.bool,
  order: PropTypes.object,
  bins: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      capacityInGrams: PropTypes.number,
      name: PropTypes.string,
      lastDelivery: PropTypes.number,
      recommendedDelivery: PropTypes.number,
      scheduledDelivery: PropTypes.number,
      shippedDelivery: PropTypes.number,
    }),
  ),
  binSetLevelInGrams: PropTypes.number,
  predictedAt: PropTypes.number,
  emptyAt: PropTypes.number,
  earliestDeliveryAt: PropTypes.number,
  runout_prevention_order_deadline_at: PropTypes.number,
  isMetric: PropTypes.bool,
  binSetId: PropTypes.string,
  lastUpdatedAt: PropTypes.number,
  // Chart data props
  step_bin_level_history: PropTypes.arrayOf(
    PropTypes.shape({
      t: PropTypes.number,
      v: PropTypes.number,
    }),
  ),
  projection_past_deliveries: PropTypes.arrayOf(
    PropTypes.shape({
      t: PropTypes.number,
      v: PropTypes.number,
    }),
  ),
  projection_with_scheduled: PropTypes.arrayOf(
    PropTypes.shape({
      t: PropTypes.number,
      v: PropTypes.number,
    }),
  ),
  projection_with_recommended: PropTypes.arrayOf(
    PropTypes.shape({
      t: PropTypes.number,
      v: PropTypes.number,
    }),
  ),
  chartStartTime: PropTypes.number,
  chartEndTime: PropTypes.number,
  // Truth data props
  knownBinLevelData: PropTypes.array,
  displayTruthData: PropTypes.bool,
  runout_prevention_bin_set_level_in_grams: PropTypes.number,
  earliest_delivery_bin_set_level_in_grams: PropTypes.number,
  perBinChartData: PropTypes.shape({
    pastByBinId: PropTypes.object,
    deliveredByBinId: PropTypes.object,
    scheduledByBinId: PropTypes.object,
    recommendedByBinId: PropTypes.object,
  }),
  perBinLevelInGrams: PropTypes.object,
};

export default WideBinSetStatusCard;
